import type { TicketStatusV3Condition as TicketStatusV3ConditionType } from "@redotech/redo-model/advanced-flow/rules-v3/schemas";
import {
  CONVERSATION_STATUSES_V3,
  type ConversationStatusV3,
} from "@redotech/redo-model/advanced-flow/rules-v3/types";
import type { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoMultiselectDropdown } from "@redotech/redo-web/arbiter-components/list/redo-multiselect-dropdown";
import { RedoSingleSelectDropdownInputButton } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo, useState } from "react";

const STATUS_DISPLAY_NAMES: Record<ConversationStatusV3, string> = {
  OPEN: "Open",
  CLOSED: "Closed",
  SNOOZED: "Snoozed",
  IN_PROGRESS: "In Progress",
  HIDDEN: "Hidden",
  ARCHIVED: "Archived",
  DELETED: "Deleted",
};

const statusItems: RedoListItem<ConversationStatusV3>[] =
  CONVERSATION_STATUSES_V3.map((status) => ({
    id: status,
    type: "text" as const,
    text: STATUS_DISPLAY_NAMES[status],
    value: status,
  }));

export const TicketStatusV3Condition = memo(function TicketStatusV3Condition({
  condition,
  onChange,
  editableFields,
}: {
  condition: TicketStatusV3ConditionType;
  onChange: (updated: TicketStatusV3ConditionType) => void;
  editableFields?: ReadonlySet<string>;
}) {
  const statusesEditable =
    editableFields === undefined || editableFields.has("statuses");

  const [dropdownButtonRef, setDropdownButtonRef] =
    useState<HTMLButtonElement | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const selectedItems: RedoListItem<ConversationStatusV3>[] = useMemo(() => {
    return (condition.statuses || []).map((status) => ({
      id: status,
      type: "text" as const,
      text: STATUS_DISPLAY_NAMES[status],
      value: status,
    }));
  }, [condition.statuses]);

  const displayItem: RedoListItem<ConversationStatusV3> | undefined =
    useMemo(() => {
      if (selectedItems.length === 0) return undefined;
      if (selectedItems.length === 1) return selectedItems[0];
      return {
        id: "display",
        type: "text" as const,
        text: `${selectedItems.length} statuses selected`,
        value: "OPEN" as ConversationStatusV3,
      };
    }, [selectedItems]);

  return (
    <Flex dir="column" gap="sm" w="full">
      <Text fontSize="xs" textColor="tertiary">
        Status
      </Text>
      <RedoSingleSelectDropdownInputButton
        disabled={!statusesEditable}
        isDropdownOpen={dropdownOpen}
        onClick={() => statusesEditable && setDropdownOpen(!dropdownOpen)}
        placeholder="Select statuses"
        ref={setDropdownButtonRef}
        selectedItem={displayItem}
        size="xs"
      />
      <RedoMultiselectDropdown
        dropdownAnchor={dropdownButtonRef}
        dropdownOpen={dropdownOpen}
        fitToAnchor
        items={statusItems}
        selectedItems={selectedItems}
        selectionVariant="checkbox"
        setDropdownOpen={setDropdownOpen}
        setSelectedItems={(items) => {
          if (!statusesEditable) return;
          onChange({ ...condition, statuses: items.map((item) => item.value) });
        }}
        size="xs"
      />
    </Flex>
  );
});
